/**
 * Hook to detect app version updates
 * Checks for new versions periodically and notifies when an update is available
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppVersionInfo } from '../types';

/**
 * Version information from the API
 */
interface VersionInfo {
  version: string;
  timestamp: number;
}

/**
 * Hook to detect app version updates
 * Checks for new versions periodically and notifies when an update is available
 *
 * @param checkInterval - Interval in milliseconds to check for updates (default: 5 minutes)
 * @param autoCheck - Whether to automatically check for updates (default: true)
 * @returns App version information with update status and reload function
 */
export function useAppVersion(
  checkInterval: number = 5 * 60 * 1000,
  autoCheck: boolean = true
): AppVersionInfo & { isChecking: boolean; checkForUpdate: () => Promise<void> } {
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  // Use ref to store the latest checkForUpdate function
  const checkForUpdateRef = useRef<(() => Promise<void>) | undefined>(undefined);

  const checkForUpdate = useCallback(async () => {
    setIsChecking(prev => {
      if (prev) return prev; // Already checking, don't start another check
      return true;
    });

    try {
      const response = await fetch('/api/version', {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        console.warn('Failed to fetch version info');
        return;
      }

      const data: VersionInfo = await response.json();
      const serverVersion = data.version;

      // Get stored version from localStorage
      const storedVersion = localStorage.getItem('app_version');

      if (!storedVersion) {
        // First time - store the current version
        localStorage.setItem('app_version', serverVersion);
        setCurrentVersion(serverVersion);
      } else if (storedVersion !== serverVersion) {
        // Version mismatch - update available
        console.log(`Update available: ${storedVersion} → ${serverVersion}`);
        setHasUpdate(true);
        setCurrentVersion(serverVersion);
      } else {
        // Same version
        setCurrentVersion(serverVersion);
        setHasUpdate(false);
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Update the ref whenever checkForUpdate changes
  checkForUpdateRef.current = checkForUpdate;

  const reload = useCallback(() => {
    // Update stored version before reload
    if (currentVersion) {
      localStorage.setItem('app_version', currentVersion);
    }

    // Force a hard reload to get the latest version
    window.location.reload();
  }, [currentVersion]);

  useEffect(() => {
    if (!autoCheck) return;

    // Check immediately on mount
    if (checkForUpdateRef.current) {
      checkForUpdateRef.current();
    }

    // Set up periodic checking
    const interval = setInterval(() => {
      if (checkForUpdateRef.current) {
        checkForUpdateRef.current();
      }
    }, checkInterval);

    return () => clearInterval(interval);
  }, [checkInterval, autoCheck]);

  // Also check on window focus (user returns to tab)
  useEffect(() => {
    if (!autoCheck) return;

    const handleFocus = () => {
      if (checkForUpdateRef.current) {
        checkForUpdateRef.current();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [autoCheck]);

  return {
    currentVersion,
    hasUpdate,
    isChecking,
    checkForUpdate,
    reload,
  };
}

